import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  useGetQuizQuestionsQuery,
  useCreateQuizQuestionMutation,
  useDeleteQuizQuestionMutation,
  useGetProductsQuery,
} from './productKnowledgeApiSlice';
import {
  Header,
  Button,
  Icon,
  Modal,
  Container,
  Table,
  Form,
  Loader,
  Select,
  Pagination,
  Input,
} from 'semantic-ui-react';
import { PAGE_SIZE } from '../../constants';
import CustomDropdown from '../../components/CustomDropdown';

const QUESTION_TYPES = [
  { key: 'MC', value: 'MC', text: '객관식 (Multiple Choice)' },
  { key: 'OE', value: 'OE', text: '주관식 (Open Ended)' },
];

const QuizQuestionModal = ({ open, onClose, products = [] }) => {
  const [createQuestion] = useCreateQuizQuestionMutation();

  const [formData, setFormData] = useState({
    product: '',
    source_evaluation: '',
    question_text: '',
    question_type: 'MC',
    answer_meta: '{}',
  });

  const productOptions = products.map(p => ({
    key: p.id,
    value: p.id,
    text: p.name,
  }));

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        answer_meta: JSON.parse(formData.answer_meta),
      };

      await createQuestion(payload).unwrap();
      onClose();
    } catch (err) {
      console.error('Failed to save question:', err);
      alert('Error saving question. Check JSON format.');
    }
  };

  const handleChange = (e, { name, value }) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>새 퀴즈 문제 추가</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field
            control={Select}
            label="관련 제품"
            options={productOptions}
            placeholder="제품 선택"
            name="product"
            value={formData.product}
            onChange={handleChange}
            search
          />
          <Form.Input
            label="소스 평가 ID (Source Eval ID)"
            name="source_evaluation"
            value={formData.source_evaluation}
            onChange={handleChange}
            type="number"
            required
          />
          <Form.TextArea
            label="질문 내용"
            name="question_text"
            value={formData.question_text}
            onChange={handleChange}
            required
          />
          <Form.Select
            label="유형"
            name="question_type"
            options={QUESTION_TYPES}
            value={formData.question_type}
            onChange={handleChange}
          />
          <Form.TextArea
            label="정답 메타데이터 (JSON)"
            name="answer_meta"
            value={formData.answer_meta}
            onChange={handleChange}
            rows={5}
            style={{ fontFamily: 'monospace' }}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>취소</Button>
        <Button primary onClick={handleSubmit}>
          저장
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const QuizQuestionManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get('page') || 1;
  const page_size = queryParams.get('page_size') || PAGE_SIZE;
  const search = queryParams.get('search');
  const ordering = queryParams.get('ordering');
  const product = queryParams.get('product');
  const question_type = queryParams.get('question_type');

  const [searchValue, setSearchValue] = useState(search || '');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter params for API
  let qp = { page, page_size, search, ordering, product, question_type };
  qp = Object.entries(qp)
    .filter(e => e[1])
    .reduce((accumulator, [k, v]) => {
      accumulator[k] = v;
      return accumulator;
    }, {});

  const { data: questionsData, isLoading } = useGetQuizQuestionsQuery(qp);
  const { data: productsData } = useGetProductsQuery({ page_size: 1000 });

  const [deleteQuestion] = useDeleteQuizQuestionMutation();

  const handleFilterChange = (key, value) => {
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleSearch = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchValue) {
      searchParams.set('search', searchValue);
    } else {
      searchParams.delete('search');
    }
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleSort = clickedColumn => {
    const searchParams = new URLSearchParams(location.search);
    const currentOrdering = searchParams.get('ordering');

    let newOrdering = clickedColumn;
    if (currentOrdering === clickedColumn) {
      newOrdering = `-${clickedColumn}`;
    } else if (currentOrdering === `-${clickedColumn}`) {
      newOrdering = clickedColumn;
    }

    searchParams.set('ordering', newOrdering);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const getSortIcon = column => {
    if (ordering === column) return 'sort up';
    if (ordering === `-${column}`) return 'sort down';
    return 'sort';
  };

  const handleEdit = question => {
    navigate(`/admin/quiz-questions/${question.id}`);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation(); // Prevent row click
    if (window.confirm('정말 이 문제를 삭제하시겠습니까?')) {
      await deleteQuestion(id);
    }
  };

  const handlePaginationChange = (e, { activePage }) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', activePage);
    if (!searchParams.get('page_size')) {
      searchParams.set('page_size', PAGE_SIZE);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  if (isLoading) return <Loader active />;

  const products = productsData?.results || [];
  const productOptions = products.map(p => ({
    key: p.id,
    text: p.name,
    value: p.id,
  }));
  const questions = questionsData?.results || [];
  const totalCount = questionsData?.count || 0;
  const totalPages = Math.ceil(totalCount / page_size);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <QuizQuestionModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        products={products}
      />

      {/* Header Section */}
      <div
        style={{
          flex: '0 0 auto',
          padding: '16px 0',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          marginBottom: isMobile ? '20px' : '0',
        }}
      >
        <Container fluid>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 20px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '40px',
                  flexShrink: 0, // stays a circle when the text beside it wraps
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#F2F2F7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon
                  name="question circle"
                  style={{ margin: 0, color: '#0091FF', fontSize: '18px' }}
                />
              </div>
              <div>
                <h2
                  style={{
                    fontSize: '17px',
                    fontWeight: '600',
                    margin: 0,
                    color: '#1d1d1f',
                  }}
                >
                  퀴즈 문제 관리
                </h2>
                <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>
                  Manage quiz questions and answers
                </p>
              </div>
            </div>
            <Button
              primary
              onClick={() => setIsModalOpen(true)}
              icon
              labelPosition="left"
              style={{
                borderRadius: '30px',
                height: '40px',
                backgroundColor: '#0091FF',
                paddingLeft: '20px',
                paddingRight: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 0,
              }}
            >
              <Icon name="add" />
              문제 추가
            </Button>
          </div>
        </Container>
      </div>

      <Container
        fluid
        style={{
          padding: isMobile ? '0 20px' : '20px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            marginBottom: '24px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '12px',
            flexWrap: 'wrap',
            alignItems: isMobile ? 'stretch' : 'center',
          }}
        >
          {/* Search Input */}
          <div
            style={{
              flex: '1 1 auto',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#F5F5F7',
              borderRadius: '12px',
              padding: '0 16px',
              height: '48px',
              gap: '12px',
              transition: 'all 0.2s ease',
            }}
          >
            <Icon
              name="search"
              style={{ color: '#86868b', margin: 0, fontSize: '16px' }}
            />
            <Input
              placeholder="검색 (질문 내용)..."
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              transparent
              fluid
              style={{
                fontSize: '14px',
                width: '100%',
                fontFamily: 'inherit',
                color: '#1d1d1f',
              }}
            />
            {searchValue && (
              <Icon
                name="close"
                link
                style={{ color: '#86868b', margin: 0, fontSize: '14px', display: 'flex', alignItems: 'center' }}
                onClick={e => {
                  e.stopPropagation();
                  setSearchValue('');
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.delete('search');
                  navigate(`${location.pathname}?${searchParams.toString()}`);
                }}
              />
            )}
          </div>

          {/* Product Filter */}
          <CustomDropdown
            placeholder="제품 필터"
            options={productOptions}
            value={product ? parseInt(product) : ''}
            onChange={value => handleFilterChange('product', value)}
            icon="cubes"
            searchable={true}
            style={{ minWidth: '200px' }}
          />

          {/* Type Filter */}
          <CustomDropdown
            placeholder="질문 유형"
            options={QUESTION_TYPES}
            value={question_type || ''}
            onChange={value => handleFilterChange('question_type', value)}
            icon="filter"
            style={{ minWidth: '150px' }}
          />
          <style>
            {`
                .custom-transparent-dropdown.ui.dropdown {
                   border: none !important;
                   background: transparent !important;
                   min-height: auto !important;
                   padding: 0 !important;
                   display: flex !important;
                   align-items: center !important;
                }
                .custom-transparent-dropdown.ui.dropdown .text {
                   color: #1d1d1f !important;
                }
                .custom-transparent-dropdown.ui.dropdown > .dropdown.icon {
                   top: auto !important;
                   position: static !important;
                   margin-left: 8px !important;
                }
                /* Remove default icon margins in inputs if any */
                .ui.input > i.icon {
                    margin: 0 !important;
                }
              `}
          </style>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid rgba(0,0,0,0.05)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <Table
              compact="very"
              selectable
              style={{ margin: 0, border: 'none' }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    style={{
                      borderBottom: '1px solid rgba(34,36,38,.15)',
                      cursor: 'pointer',
                    }}
                    sorted={
                      ordering &&
                      (ordering === 'created_at' || ordering === '-created_at')
                        ? ordering === 'created_at'
                          ? 'ascending'
                          : 'descending'
                        : null
                    }
                    onClick={() => handleSort('created_at')}
                  >
                    생성날짜 <Icon name={getSortIcon('created_at')} />
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{
                      borderBottom: '1px solid rgba(34,36,38,.15)',
                      cursor: 'pointer',
                    }}
                    sorted={
                      ordering &&
                      (ordering === 'product' || ordering === '-product')
                        ? ordering === 'product'
                          ? 'ascending'
                          : 'descending'
                        : null
                    }
                    onClick={() => handleSort('product')}
                  >
                    제품 <Icon name={getSortIcon('product')} />
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{
                      borderBottom: '1px solid rgba(34,36,38,.15)',
                      cursor: 'pointer',
                    }}
                    sorted={
                      ordering &&
                      (ordering === 'question_text' ||
                        ordering === '-question_text')
                        ? ordering === 'question_text'
                          ? 'ascending'
                          : 'descending'
                        : null
                    }
                    onClick={() => handleSort('question_text')}
                  >
                    질문 <Icon name={getSortIcon('question_text')} />
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{
                      borderBottom: '1px solid rgba(34,36,38,.15)',
                      cursor: 'pointer',
                    }}
                    sorted={
                      ordering &&
                      (ordering === 'question_type' ||
                        ordering === '-question_type')
                        ? ordering === 'question_type'
                          ? 'ascending'
                          : 'descending'
                        : null
                    }
                    onClick={() => handleSort('question_type')}
                  >
                    유형 <Icon name={getSortIcon('question_type')} />
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}
                  >
                    사용자
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}
                  >
                    채팅 세션
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{
                      borderBottom: '1px solid rgba(34,36,38,.15)',
                      textAlign: 'center',
                    }}
                  >
                    액션
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {questions.map(q => {
                  const productObj = products.find(p => p.id === q.product);
                  return (
                    <Table.Row
                      key={q.id}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Cell>
                        {q.created_at
                          ? new Date(q.created_at).toLocaleString('ko-KR')
                          : '-'}
                      </Table.Cell>
                      <Table.Cell>
                        {productObj ? productObj.name : q.product || '-'}
                      </Table.Cell>
                      <Table.Cell>
                        {q.question_text.length > 60
                          ? `${q.question_text.substring(0, 60)}...`
                          : q.question_text}
                      </Table.Cell>
                      <Table.Cell>
                        {q.question_type === 'MC' ? '객관식' : '주관식'}
                      </Table.Cell>
                      <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                        {/* username is a Firebase UID for social sign-ins — show the person */}
                        {q.user_display_name || q.user_username || '-'}
                        {q.user_contractor && (
                          <div style={{ fontSize: '12px', color: '#86868b' }}>{q.user_contractor}</div>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {q.chat_session_id ? (
                          <div onClick={e => e.stopPropagation()}>
                            <Button
                              as={Link}
                              to={`/chatsessions/${q.chat_session_id}`}
                              size="mini"
                              basic
                              content="보기"
                            />
                          </div>
                        ) : (
                          '-'
                        )}
                      </Table.Cell>
                      <Table.Cell style={{ textAlign: 'right' }}>
                        {/* <Button
                          content="삭제"
                          size="mini"
                          style={{ borderRadius: '30px' }}
                          // color="red"
                          onClick={e => handleDelete(e, q.id)}
                        /> */}
                        <Button
                          size='mini'
                            icon="eye"
                            content="상세"
                            onClick={() => navigate(`/admin/quiz-questions/${q.id}`)}
                            style={{ borderRadius: '30px' }}
                        />

                      </Table.Cell>
                    </Table.Row>
                  );
                })}
                {questions.length === 0 && (
                  <Table.Row>
                    <Table.Cell
                      colSpan="7"
                      textAlign="center"
                      style={{ padding: '40px' }}
                    >
                      <div style={{ color: '#86868b' }}>
                        검색 결과가 없습니다.
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>

          {/* Pagination */}
          {!isMobile && totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                borderTop: '1px solid rgba(34,36,38,.15)',
                backgroundColor: '#f8f9fa',
              }}
            >
              <Pagination
                activePage={page}
                onPageChange={handlePaginationChange}
                totalPages={totalPages}
                size="mini"
                ellipsisItem={{
                  content: <Icon name="ellipsis horizontal" />,
                  icon: true,
                }}
                firstItem={{
                  content: <Icon name="angle double left" />,
                  icon: true,
                }}
                lastItem={{
                  content: <Icon name="angle double right" />,
                  icon: true,
                }}
                prevItem={{ content: <Icon name="angle left" />, icon: true }}
                nextItem={{ content: <Icon name="angle right" />, icon: true }}
              />
            </div>
          )}

          {/* Mobile Pagination */}
          {isMobile && totalPages > 1 && (
            <div
              style={{
                marginTop: '20px',
                textAlign: 'center',
                paddingBottom: '20px',
              }}
            >
              <Pagination
                activePage={page}
                onPageChange={handlePaginationChange}
                totalPages={totalPages}
                firstItem={{
                  content: <Icon name="angle double left" />,
                  icon: true,
                }}
                lastItem={{
                  content: <Icon name="angle double right" />,
                  icon: true,
                }}
                prevItem={{ content: <Icon name="angle left" />, icon: true }}
                nextItem={{ content: <Icon name="angle right" />, icon: true }}
              />
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default QuizQuestionManagement;
